import { inject, Injectable } from '@angular/core';

// Type-only: erased at compile time, so MSAL stays out of the initial bundle. The
// implementation is pulled in by dynamic import when the user actually clicks the button.
import type { IPublicClientApplication } from '@azure/msal-browser';

import { ENVIRONMENT } from '../environments/environment.token';

import { oauthCallbackUrl } from './oauth/oauth-popup';

// Work and school accounts only. Not '/common', which would also admit personal Microsoft
// accounts that the multi-tenant app registration then rejects with an opaque error.
const MICROSOFT_AUTHORITY = 'https://login.microsoftonline.com/organizations';

// The backend only needs an identity; it reads the email from the ID token and never calls
// Microsoft Graph on the user's behalf.
const SCOPES = [ 'openid', 'profile', 'email' ];

// Mirrors BrowserAuthErrorCodes.userCancelled from @azure/msal-browser.
const USER_CANCELLED_ERROR_CODE = 'user_cancelled';

// Always show the account picker rather than silently reusing Microsoft's SSO session.
// Sign-in requires the Microsoft email to match an existing user, so a browser signed into
// the wrong account would otherwise get an unexplained 401 with no way to choose another —
// the picker is what makes that recoverable. It also lets someone with a work and a personal
// account pick deliberately instead of getting whichever Microsoft last used.
const ACCOUNT_PROMPT = 'select_account';

/**
 * Obtains a Microsoft Entra ID token in the browser. The token is not a session: it is proof
 * of identity that the backend validates before minting our own session.
 */
@Injectable({
  providedIn: 'root',
})
export class MicrosoftAuthService {
  private environment = inject(ENVIRONMENT);
  private instance?: Promise<IPublicClientApplication>;

  /** Whether Microsoft sign-in is configured for this environment. */
  get isConfigured(): boolean {
    return !!this.environment.entraClientId;
  }

  /**
   * Opens the Microsoft sign-in popup and resolves with the ID token.
   * Resolves with null when the user closes the popup, which is a choice rather than a failure.
   */
  async signIn(): Promise<string | null> {
    try {
      const instance = await this.getInstance();
      const result = await instance.loginPopup({ scopes: SCOPES, prompt: ACCOUNT_PROMPT });

      return result.idToken;
    } catch (error) {
      if (this.isUserCancellation(error)) {
        return null;
      }

      throw error;
    }
  }

  // Created on first use, not on construction: the service is injected wherever the login
  // component is, but most visits never click the button.
  private getInstance(): Promise<IPublicClientApplication> {
    this.instance ??= this.createInstance();

    return this.instance;
  }

  private async createInstance(): Promise<IPublicClientApplication> {
    // Loaded on demand. MSAL is ~200 kB and most visitors never sign in with Microsoft,
    // so keeping it out of the initial bundle costs one network round trip on click.
    const { PublicClientApplication } = await import('@azure/msal-browser');

    const instance = new PublicClientApplication({
      auth: {
        clientId: this.environment.entraClientId,
        authority: MICROSOFT_AUTHORITY,
        // The shared OAuth callback page, not the app origin. Landing on the origin boots
        // Angular, whose router destroys the authorization response before it can be read.
        redirectUri: oauthCallbackUrl(),
      },
      cache: {
        // Scoped to the tab. Our own session is the thing that persists; MSAL state is only
        // needed for the duration of the popup handshake.
        cacheLocation: 'sessionStorage',
      },
    });

    await instance.initialize();

    return instance;
  }

  // Only a closed popup counts. A blocked popup (popup_window_error) is a real failure the
  // user has to be told about, since nothing visible happened when they clicked.
  // Compared as a literal rather than via BrowserAuthErrorCodes, which would drag the whole
  // MSAL module into the initial bundle for one string.
  private isUserCancellation(error: unknown): boolean {
    const code = (error as { errorCode?: string } | null)?.errorCode;

    return code === USER_CANCELLED_ERROR_CODE;
  }
}
